import * as THREE from 'three/webgpu';

/**
 * 草图实体变换命令枚举。
 */
export enum SketchEntitiesTransformCommand {
    /** 移动实体命令。 */
    Move = 'sketch-move-entities',
    /** 复制实体命令。 */
    Copy = 'sketch-copy-entities',
    /** 旋转实体命令。 */
    Rotate = 'sketch-rotate-entities',
    /** 镜向实体命令。 */
    Mirror = 'sketch-mirror-entities',
}

/**
 * 草图实体变换输入模式。
 */
export enum SketchEntitiesTransformInputMode {
    /** From/To 点输入模式。 */
    FromTo = 'from-to',
    /** X/Y 平移距离输入模式。 */
    Coordinate = 'coordinate',
}

/**
 * 草图实体变换选择角色。
 */
export enum SketchEntitiesTransformSelectionRole {
    /** 未激活任何视口选择。 */
    None = 'none',
    /** 选择待变换实体。 */
    Entities = 'entities',
    /** 选择起点。 */
    FromPoint = 'from-point',
    /** 选择终点。 */
    ToPoint = 'to-point',
    /** 选择旋转中心。 */
    RotationCenter = 'rotation-center',
    /** 选择镜向轴。 */
    MirrorAxis = 'mirror-axis',
}

/**
 * 草图实体变换阶段枚举。
 */
export enum SketchEntitiesTransformPhase {
    /** 正在选择待变换实体。 */
    SelectEntities = 'selectEntities',
    /** 正在选择参考点。 */
    SelectReference = 'selectReference',
    /** 参数已完整。 */
    Ready = 'ready',
    /** 正在预览。 */
    Previewing = 'previewing',
    /** 已提交。 */
    Committed = 'committed',
    /** 已取消。 */
    Cancelled = 'cancelled',
}

//------ 移动实体保留几何关系 开始------
/**
 * 草图实体变换几何关系保留模式。
 *
 * 设计说明：
 * 1. SOLIDWORKS 的 `Keep relations` 是实体变换命令的有限选项，不应使用裸 boolean 在底层流转；
 * 2. `KeepRelations` 表示保持所有现有关系；
 * 3. `BreakExternalRelations` 表示断开所选实体与未选实体之间的跨边界关系，同时保留选择域内部关系。
 */
export enum SketchEntitiesTransformRelationRetentionMode {
    /** 保留现有几何关系。 */
    KeepRelations = 'keep-relations',
    /** 断开所选实体与未选实体之间的外部几何关系。 */
    BreakExternalRelations = 'break-external-relations',
}
//------ 移动实体保留几何关系 结束------

/**
 * 已选实体展示项。
 */
export interface SketchEntitiesTransformSelectedItem {
    /** 实体 ID。 */
    id: string;
    /** 展示标签。 */
    label: string;
}

/**
 * 参考点展示项。
 */
export interface SketchEntitiesTransformPointItem {
    /** 参考点 ID。 */
    id: string;
    /** 展示标签。 */
    label: string;
    /** 世界坐标。 */
    position: THREE.Vector3;
}

/**
 * 控制器状态快照。
 */
export interface SketchEntitiesTransformControllerSnapshot {
    /** 当前命令是否激活。 */
    active: boolean;
    /** 当前命令类型。 */
    command: SketchEntitiesTransformCommand | null;
    /** 当前阶段。 */
    phase: SketchEntitiesTransformPhase;
    /** 当前输入模式。 */
    inputMode: SketchEntitiesTransformInputMode;
    /** 当前选择角色。 */
    activeSelectionRole: SketchEntitiesTransformSelectionRole;
    /** 几何关系保留模式。 */
    relationRetentionMode: SketchEntitiesTransformRelationRetentionMode;
    /** 当前已选实体列表。 */
    selectedItems: SketchEntitiesTransformSelectedItem[];
    /** From 点。 */
    fromPoint: SketchEntitiesTransformPointItem | null;
    /** To 点。 */
    toPoint: SketchEntitiesTransformPointItem | null;
    /** 旋转中心。 */
    rotationCenter: SketchEntitiesTransformPointItem | null;
    /** 镜向轴；非镜向命令固定为 null。 */
    mirrorAxis: SketchEntitiesTransformSelectedItem | null;
    /** 是否复制镜向实体；非镜向命令固定为 false。 */
    copyEnabled: boolean;
    /** X 输入值，移动实体中表示 X 方向平移距离。 */
    targetX: number | null;
    /** Y 输入值，移动实体中表示 Y 方向平移距离。 */
    targetY: number | null;
    /** 旋转角度。 */
    angleDeg: number | null;
    /** 是否允许确认。 */
    canConfirm: boolean;
    /** 是否允许重复。 */
    canRepeat: boolean;
}

/**
 * 重复变换记录。
 */
export interface SketchEntitiesTransformRepeatRecord {
    /** 源实体 ID 列表。 */
    sourceEntityIds: string[];
    /** 变换矩阵。 */
    matrix: THREE.Matrix4;
}
